import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { readListState, persistListState, useListScroll } from '../lib/listState';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Search, Ticket as TicketIcon, Filter, Archive, Trash2, AtSign, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import {
  TICKET_STATUSES,
  TICKET_CATEGORIES,
  TICKET_CATEGORY_OPTIONS,
  URGENCY_LEVELS,
  formatDate,
} from '../lib/constants';

// Terminal statuses = riwayat. Completed (Selesai) tickets stay in Aktif until invoice generated.
const HISTORY_STATUSES = ['invoiced', 'rejected', 'cancelled'];

const TicketsPage = () => {
  const { user } = useAuth();
  const savedListState = useRef(readListState('tickets')).current;
  const [tickets, setTickets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(savedListState.search || '');
  const [statusFilter, setStatusFilter] = useState(savedListState.statusFilter || 'all');
  const [categoryFilter, setCategoryFilter] = useState(savedListState.categoryFilter || 'all');
  const [locationFilter, setLocationFilter] = useState(savedListState.locationFilter || 'all');
  const [dateFrom, setDateFrom] = useState(savedListState.dateFrom || '');
  const [dateTo, setDateTo] = useState(savedListState.dateTo || '');
  const [activeTab, setActiveTab] = useState(savedListState.activeTab || 'active'); // 'active' | 'history' | 'archive'
  const [archivedTickets, setArchivedTickets] = useState([]);
  const [mentionByTicket, setMentionByTicket] = useState({});

  const handleExportExcel = async (scope) => {
    try {
      const res = await api.get(`/exports/tickets?scope=${scope}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `tickets-${scope}-${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export berhasil diunduh');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal export');
    }
  };

  useEffect(() => {
    loadTickets();
    loadLocations();
    api.get('/notifications/summary').then((r) => {
      setMentionByTicket(r.data?.by_ticket || {});
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab === 'archive' && user?.role === 'super_admin') {
      loadArchived();
    }
  }, [activeTab, user]);

  // Persist search/filter/tab so returning from a ticket keeps the same view
  useEffect(() => {
    persistListState('tickets', { search, statusFilter, categoryFilter, locationFilter, dateFrom, dateTo, activeTab });
  }, [search, statusFilter, categoryFilter, locationFilter, dateFrom, dateTo, activeTab]);

  // Restore scroll position once the list has rendered (desktop & mobile use window scroll)
  useListScroll('tickets', !loading);

  const loadArchived = async () => {
    try {
      const { data } = await api.get('/tickets?archived=true');
      setArchivedTickets(data);
    } catch (error) {
      console.error(error);
    }
  };

  const loadLocations = async () => {
    try {
      const { data } = await api.get('/locations');
      setLocations(data.filter((l) => !l.is_warehouse));
    } catch (error) {
      console.error(error);
    }
  };

  const loadTickets = async () => {
    try {
      const { data } = await api.get('/tickets');
      setTickets(data);
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const activeCount = tickets.filter((t) => !HISTORY_STATUSES.includes(t.status)).length;
  const historyCount = tickets.filter((t) => HISTORY_STATUSES.includes(t.status)).length;

  const handleClearHistory = async () => {
    if (!window.confirm('Bersihkan semua tiket yang Ditolak/Dibatalkan? Tiket Selesai (dengan invoice) TIDAK akan dihapus. Aksi ini tidak dapat dibatalkan.')) return;
    try {
      const { data } = await api.post('/tickets/history/clear');
      toast.success(data.message);
      loadTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal membersihkan riwayat');
    }
  };

  const filteredTickets = (activeTab === 'archive' ? archivedTickets : tickets).filter((ticket) => {
    // Tab filter: active tab hides terminal statuses; history tab only shows terminal statuses
    let inTab = true;
    if (activeTab === 'history') {
      inTab = HISTORY_STATUSES.includes(ticket.status);
    } else if (activeTab === 'active') {
      inTab = !HISTORY_STATUSES.includes(ticket.status);
    }
    // Archive tab: all archived tickets shown (no additional status filter)
    const matchesSearch =
      ticket.title?.toLowerCase().includes(search.toLowerCase()) ||
      ticket.ticket_number?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || ticket.category === categoryFilter;
    const matchesLocation = locationFilter === 'all' || ticket.location_id === locationFilter;
    // Date range filter (based on created_at)
    let matchesDate = true;
    if (dateFrom || dateTo) {
      const created = ticket.created_at ? new Date(ticket.created_at).getTime() : 0;
      if (dateFrom && created < new Date(`${dateFrom}T00:00:00`).getTime()) matchesDate = false;
      if (dateTo && created > new Date(`${dateTo}T23:59:59`).getTime()) matchesDate = false;
    }
    return inTab && matchesSearch && matchesStatus && matchesCategory && matchesLocation && matchesDate;
  });

  return (
    <Layout>
      <div className="space-y-6" data-testid="tickets-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Ticketing</p>
            <h1 className="text-4xl font-medium tracking-tight">Daftar Tiket</h1>
            <p className="text-muted-foreground mt-1">Kelola semua tiket permintaan dari cabang</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => handleExportExcel(activeTab)}
              data-testid="export-tickets-button"
            >
              <FileDown className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
            {['manager_cabang', 'super_admin', 'admin_maintenance', 'admin_it', 'admin_purchasing'].includes(user?.role) && (
              <Link to="/tickets/new">
                <Button className="bg-primary hover:bg-primary/90" data-testid="create-ticket-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Buat Tiket Baru
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Tabs: Aktif vs Riwayat */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setStatusFilter('all'); }}>
            <TabsList data-testid="ticket-tabs">
              <TabsTrigger value="active" data-testid="tab-active">
                <TicketIcon className="w-4 h-4 mr-2" />
                Aktif ({activeCount})
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history">
                <Archive className="w-4 h-4 mr-2" />
                Riwayat ({historyCount})
              </TabsTrigger>
              {user?.role === 'super_admin' && (
                <TabsTrigger value="archive" data-testid="tab-archive">
                  <Archive className="w-4 h-4 mr-2" />
                  Arsip
                </TabsTrigger>
              )}
            </TabsList>
          </Tabs>
          {activeTab === 'history' && user?.role === 'super_admin' && historyCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearHistory}
              className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
              data-testid="clear-history-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Bersihkan Ditolak/Dibatalkan
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Cari nomor tiket atau judul..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="ticket-search-input"
                />
              </div>
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger data-testid="location-filter-select">
                  <SelectValue placeholder="Filter Lokasi" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Lokasi</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter-select">
                  <SelectValue placeholder="Filter Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Status</SelectItem>
                  {Object.entries(TICKET_STATUSES)
                    .filter(([value]) =>
                      activeTab === 'history'
                        ? HISTORY_STATUSES.includes(value)
                        : !HISTORY_STATUSES.includes(value)
                    )
                    .map(([value, { label }]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger data-testid="category-filter-select">
                  <SelectValue placeholder="Filter Kategori" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Kategori</SelectItem>
                  {TICKET_CATEGORY_OPTIONS.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Dari Tanggal</label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-9"
                  data-testid="ticket-filter-from"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Sampai</label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-9"
                  data-testid="ticket-filter-to"
                />
              </div>
              {(dateFrom || dateTo) && (
                <div className="flex items-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setDateFrom(''); setDateTo(''); }}
                    className="h-9"
                    data-testid="ticket-filter-clear-date"
                  >
                    Reset Tanggal
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tickets List */}
        <Card className="border-border">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                <TicketIcon className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium mb-1">
                  {activeTab === 'archive'
                    ? 'Arsip masih kosong'
                    : activeTab === 'history'
                      ? 'Belum ada riwayat'
                      : 'Tidak ada tiket aktif'}
                </p>
                <p className="text-sm">
                  {activeTab === 'archive'
                    ? 'Tiket yang sudah diselesaikan/dibatalkan > 30 hari akan otomatis pindah ke sini'
                    : activeTab === 'history'
                      ? 'Tiket yang sudah selesai/dibatalkan akan muncul di sini'
                      : 'Belum ada tiket yang sesuai dengan filter'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filteredTickets.map((ticket) => {
                  const pingCount = mentionByTicket[ticket.ticket_number] || 0;
                  return (
                  <Link
                    key={ticket.ticket_number}
                    to={`/tickets/${ticket.ticket_number}`}
                    className={`block p-5 hover:bg-secondary/50 transition-colors relative ${
                      pingCount > 0 ? 'bg-red-50/60 border-l-4 border-l-red-500' : ''
                    }`}
                    data-testid={`ticket-row-${ticket.ticket_number}`}
                  >
                    {pingCount > 0 && (
                      <div
                        className="absolute top-3 right-3 flex items-center gap-1.5 bg-red-500 text-white px-2.5 py-1 rounded-full text-xs font-semibold shadow-md animate-pulse"
                        data-testid={`mention-badge-${ticket.ticket_number}`}
                      >
                        <AtSign className="w-3 h-3" />
                        <span>Anda Di-ping{pingCount > 1 ? ` (${pingCount})` : ''}</span>
                      </div>
                    )}
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <span className={`font-mono text-xs px-2 py-1 rounded inline-flex items-center gap-1.5 ${
                            pingCount > 0 ? 'bg-red-100 text-red-800 font-semibold' : 'bg-secondary'
                          }`}>
                            {pingCount > 0 && (
                              <span
                                className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse"
                                data-testid={`mention-dot-${ticket.ticket_number}`}
                              />
                            )}
                            {ticket.ticket_number}
                          </span>
                          <Badge variant="outline" className={URGENCY_LEVELS[ticket.urgency]?.color}>
                            {URGENCY_LEVELS[ticket.urgency]?.label}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            {TICKET_CATEGORIES[ticket.category]}
                          </Badge>
                        </div>
                        <h3 className="font-medium text-lg tracking-tight mb-1">{ticket.title}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
                          {ticket.description}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>{ticket.location_name}</span>
                          <span>•</span>
                          <span>{ticket.creator_name}</span>
                          <span>•</span>
                          <span>{formatDate(ticket.created_at)}</span>
                        </div>
                      </div>
                      <Badge className={`${TICKET_STATUSES[ticket.status]?.color} whitespace-nowrap`}>
                        {TICKET_STATUSES[ticket.status]?.label || ticket.status}
                      </Badge>
                    </div>
                  </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default TicketsPage;
